import { Button } from '@repo/ui/components/ui/button'
import { Check, Clock, Copy, Mail, Route } from 'lucide-react'
import { useState } from 'react'
import { GithubIcon, XIcon } from '@/components/icons'

const contactItems = [
  {
    label: 'General',
    email: 'hello@zippity.app',
  },
  {
    label: 'Support',
    email: 'support@zippity.app',
  },
  {
    label: 'Partners',
    email: 'partners@zippity.app',
  },
]

const canonicalHandlePattern = /^@[A-Za-z0-9._-]+$/

type SocialConfig = {
  icon: typeof GithubIcon
  label: string
  handle: string
  toUrl: (handleWithoutAt: string, fullHandle: string) => string
}

const socialConfigs: SocialConfig[] = [
  {
    icon: GithubIcon,
    label: 'GitHub',
    handle: '@zippity-app',
    toUrl: (handleWithoutAt) => `https://github.com/${handleWithoutAt}`,
  },
  {
    icon: XIcon,
    label: 'X',
    handle: '@zippityapp',
    toUrl: (handleWithoutAt) => `https://x.com/${handleWithoutAt}`,
  },
]

const socialLinks = socialConfigs
  .map((social) => {
    if (!canonicalHandlePattern.test(social.handle)) return null
    const handleWithoutAt = social.handle.slice(1)
    return {
      icon: social.icon,
      label: social.label,
      display: social.handle,
      href: social.toUrl(handleWithoutAt, social.handle),
    }
  })
  .filter((social): social is NonNullable<typeof social> => social !== null)

export function ContactInfo() {
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null)

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopiedEmail(text)
    setTimeout(() => setCopiedEmail(null), 2000)
  }

  return (
    <aside className="rounded-sm border border-border/75 bg-background/72 p-6 backdrop-blur-md md:p-7">
      <div className="border-b border-border/70 pb-6">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">Response</p>
        <div className="mt-5 grid gap-4">
          <div className="flex gap-3">
            <Clock className="mt-0.5 size-5 shrink-0 text-foreground" strokeWidth={1.6} />
            <div>
              <p className="font-medium">Usually within one business day</p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Include your service area and typical week if you want concrete scheduling feedback.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Route className="mt-0.5 size-5 shrink-0 text-foreground" strokeWidth={1.6} />
            <div>
              <p className="font-medium">Route context helps</p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                The best questions include where the day starts, where it ends, and where it gets
                messy.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="my-7 space-y-5">
        {contactItems.map((item) => (
          <div key={item.email} className="group">
            <div className="mb-2 text-sm text-muted-foreground">{item.label}</div>
            <Button
              variant="ghost"
              onClick={() => copyToClipboard(item.email)}
              className="h-auto max-w-full justify-start gap-3 rounded-full px-0 py-1 text-base font-medium transition-colors hover:bg-transparent hover:text-primary md:text-lg"
            >
              <Mail className="h-5 w-5 flex-shrink-0 text-foreground" />
              <span className="truncate">{item.email}</span>
              {copiedEmail === item.email ? (
                <Check className="h-4 w-4 flex-shrink-0 text-primary" />
              ) : (
                <Copy className="h-4 w-4 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              )}
            </Button>
          </div>
        ))}
      </div>

      <div className="border-t border-border/70 pt-7">
        <div className="mb-5 text-sm text-muted-foreground">Follow the build</div>
        <div className="space-y-4">
          {socialLinks.map((social) => (
            <a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 text-base font-medium transition-colors hover:text-primary"
            >
              <social.icon className="h-5 w-5 text-muted-foreground" />
              <span>{social.display}</span>
              <svg
                className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                role="img"
                aria-label="External link"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          ))}
        </div>
      </div>
    </aside>
  )
}
